import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, message, Space, Tag, Avatar, Row, Col, Typography, Tabs } from 'antd';
import { UserOutlined, ReloadOutlined, ClockCircleOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { apiNvTeamDiemDanh, apiGetAllUser } from '../services/authService';
import BangChamCongComponent from './BangChamCongComponent';
import { getTotalWorkHours } from '../utils/attendance';
import dayjs from 'dayjs';

const { Text } = Typography;

// Tỉ lệ cột theo bộ phận: Công trình 2, Máy tính 2, Dự án 1, Photocopy 1 (trên lưới 24 cột)
// subCols: số cột con để card bên trong giữ nguyên kích thước bằng nhau ở mọi bộ phận
const DEPARTMENTS_CONFIG = [
    { key: 'Công trình', span: 8, subCols: 2, color: '#fa8c16', bg: '#fff7e6', border: '#ffd591' },
    { key: 'Máy tính', span: 8, subCols: 2, color: '#1677ff', bg: '#e6f4ff', border: '#91caff' },
    { key: 'Dự án', span: 4, subCols: 1, color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f' },
    { key: 'Photocopy', span: 4, subCols: 1, color: '#722ed1', bg: '#f9f0ff', border: '#d3adf7' },
];

// Ưu tiên sắp xếp trong từng bộ phận: đang làm (0) > đã về (1) > vắng (2)
const getAttendancePriority = (member) => {
    if (member.thoiGianVao && !member.thoiGianRa) return 0;
    if (member.thoiGianVao && member.thoiGianRa) return 1;
    return 2;
};

const DiemDanhComponent = () => {
    const [loading, setLoading] = useState(false);
    const [diemDanhData, setDiemDanhData] = useState([]);
    const [boPhan, setBoPhan] = useState('');
    const [ngayHienTai] = useState(dayjs());

    const renderMemberCard = (member, index, deptColor) => {
        const gioVao = member.thoiGianVao ? dayjs(member.thoiGianVao) : null;
        const gioRa = member.thoiGianRa ? dayjs(member.thoiGianRa) : null;

        // Kiểm tra có điểm danh hay không
        const hasAttendance = member.image && member.thoiGianVao;
        const cardColor = hasAttendance ? '#f6ffed' : '#fff2f0'; // Xanh nhạt hoặc đỏ nhạt

        const getStatusTag = () => {
            const tagStyle = { margin: 0, fontSize: '10px', lineHeight: '16px', flexShrink: 0 };
            if (hasAttendance) {
                if (gioVao && gioRa) {
                    return <Tag color="green" style={tagStyle}>Đã về</Tag>;
                } else {
                    return <Tag color="green" style={tagStyle}>Đang làm</Tag>;
                }
            } else {
                return <Tag color="red" style={tagStyle}>Vắng</Tag>;
            }
        };

        const getTotalHours = () => {
            const tongGio = getTotalWorkHours(member.thoiGianVao, member.thoiGianRa);
            if (tongGio !== null) {
                return `${tongGio.toFixed(1)}h`;
            }
            return hasAttendance ? 'Đang làm' : '-';
        };

        return (
                <Card
                    key={member.id || index}
                    hoverable
                    styles={{
                        body: { padding: '10px 12px' }
                    }}
                    style={{
                        borderRadius: '10px',
                        backgroundColor: cardColor,
                        border: `1px solid ${deptColor}`,
                        height: '100%',
                        minWidth: 0,
                    }}
                >
                    {/* Header: avatar + tên trên 1 hàng ngang */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Avatar size={28} src={member.avatar} icon={<UserOutlined />} style={{ flexShrink: 0 }} />
                        <Text strong style={{ flex: 1, minWidth: 0, fontSize: '13px', color: '#0D9488', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {member.username}
                        </Text>
                    </div>
                    {/* Bộ phận + trạng thái (thay cho ID) trên 1 hàng */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginTop: '4px' }}>
                        <Text type="secondary" style={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: '10px',
                            lineHeight: 1.2,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}>
                            {member.part || member.department || 'N/A'}
                        </Text>
                        {getStatusTag()}
                    </div>

                    {/* Giờ vào / ra / tổng giờ trên 1 hàng */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: '1px solid rgba(0,0,0,0.06)'
                    }}>
                        <div>
                            <Text type="secondary" style={{ fontSize: '10px' }}><ClockCircleOutlined /> Vào</Text>
                            <Text strong style={{ display: 'block', fontSize: '12px', lineHeight: 1.3 }}>
                                {gioVao ? gioVao.format('HH:mm') : '-'}
                            </Text>
                        </div>
                        <div>
                            <Text type="secondary" style={{ fontSize: '10px' }}>Ra</Text>
                            <Text strong style={{ display: 'block', fontSize: '12px', lineHeight: 1.3 }}>
                                {gioRa ? gioRa.format('HH:mm') : '-'}
                            </Text>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <Text type="secondary" style={{ fontSize: '10px' }}>Tổng</Text>
                            <Text strong style={{ display: 'block', fontSize: '12px', lineHeight: 1.3, color: hasAttendance ? '#52c41a' : '#999' }}>
                                {getTotalHours()}
                            </Text>
                        </div>
                    </div>

                    {/* Phần tọa độ - luôn chiếm chỗ để các card cao bằng nhau */}
                    <div style={{ marginTop: '6px', minHeight: '16px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {member.toa_do && (
                            <>
                                <EnvironmentOutlined style={{ fontSize: '10px', color: '#999' }} />
                                <Text type="secondary" style={{ fontSize: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {member.toa_do}
                                </Text>
                            </>
                        )}
                    </div>
                </Card>
        );
    };

    // Function để nối dữ liệu user với điểm danh
    const mergeUserWithAttendance = (users, attendance) => {
        return users.map(user => {
            // Tìm thông tin điểm danh của user này
            const userAttendance = attendance.find(att => att.user_id === user.id);

            if (userAttendance) {
                // Nếu có điểm danh, nối thông tin
                return {
                    ...user,
                    ...userAttendance,
                    // Giữ avatar từ user, không dùng image từ attendance
                    avatar: user.avatar
                };
            } else {
                // Nếu không có điểm danh, chỉ trả về thông tin user
                return {
                    ...user,
                    thoiGianVao: null,
                    thoiGianRa: null,
                    image: null,
                    toa_do: null
                };
            }
        });
    };

    useEffect(() => {
        // Đặt bộ phận mặc định là tất cả 3 bộ phận
        setBoPhan('Tất cả');
    }, []);

    // Function để load tất cả dữ liệu từ 4 bộ phận
    const loadAllData = useCallback(async () => {
        setLoading(true);
        try {
            const departments = ['Máy tính', 'Công trình', 'Dự án', 'Photocopy'];

            // Load users và attendance từ cả 4 bộ phận song song
            const [usersResponse, ...attendanceResponses] = await Promise.all([
                apiGetAllUser(),
                ...departments.map(dept => apiNvTeamDiemDanh(dept, ngayHienTai.format('YYYY-MM-DD')))
            ]);

            console.log('=== LOAD ALL DATA ===');
            console.log('Users Response:', usersResponse);
            console.log('Attendance Responses:', attendanceResponses);

            // Xử lý dữ liệu users - API trả về response trực tiếp
            const allUsersData = Array.isArray(usersResponse) ? usersResponse : [];

            // Lọc users từ 4 bộ phận
            const allDepartmentUsers = allUsersData.filter(user =>
                departments.includes(user.part)
            );

            // Gộp tất cả dữ liệu điểm danh từ 4 bộ phận
            let allAttendanceData = [];
            attendanceResponses.forEach((response, index) => {
                const attendanceArray = response.data && Array.isArray(response.data) ? response.data : [];
                allAttendanceData = [...allAttendanceData, ...attendanceArray];
                console.log(`Điểm danh ${departments[index]}:`, attendanceArray.length);
            });

            console.log('=== PROCESSED DATA ===');
            console.log('Tổng users:', allUsersData.length);
            console.log('Users 4 bộ phận:', allDepartmentUsers.length);
            console.log('Tổng điểm danh hôm nay:', allAttendanceData.length);
            console.log('====================');

            // Nối dữ liệu và lưu trực tiếp
            const mergedData = mergeUserWithAttendance(allDepartmentUsers, allAttendanceData);
            setDiemDanhData(mergedData);

            message.success(`Hiển thị ${allDepartmentUsers.length} nhân viên từ 4 bộ phận (${allAttendanceData.length} có điểm danh)`);
        } catch (error) {
            console.log('=== LOAD ALL DATA ERROR ===');
            console.log('Error:', error);
            message.error('Không thể tải dữ liệu');
        } finally {
            setLoading(false);
        }
    }, [ngayHienTai]);

    useEffect(() => {
        // Auto load data từ tất cả bộ phận
        loadAllData();
    }, [loadAllData]);

    const renderDiemDanhHomNay = () => (
            <Card
                title="Danh sách thành viên điểm danh"
                style={{ marginBottom: '16px' }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '16px' }}>
                    <Space size="large" wrap>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ fontWeight: 'bold', marginRight: '8px' }}>Bộ phận:</span>
                            <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                                Tất cả (Máy tính, Công trình, Dự án, Photocopy)
                            </Tag>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ fontWeight: 'bold', marginRight: '8px' }}>Ngày:</span>
                            <Tag color="green" style={{ fontSize: '14px', padding: '4px 12px' }}>
                                {ngayHienTai.format('DD/MM/YYYY')}
                            </Tag>
                        </div>
                    </Space>

                    <Button
                        type="primary"
                        icon={<ReloadOutlined />}
                        onClick={loadAllData}
                        loading={loading}
                    >
                        Tải lại
                    </Button>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <Text>Đang tải dữ liệu...</Text>
                    </div>
                ) : (
                    <>
                        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                Bộ phận: <Text strong style={{ color: '#0D9488' }}>Tất cả (Máy tính, Công trình, Dự án, Photocopy)</Text>
                            </Text>
                            <Text type="secondary">
                                Tổng {diemDanhData.length} thành viên • {diemDanhData.filter(m => m.thoiGianVao).length} có điểm danh
                            </Text>
                        </div>
                        <Row gutter={[12, 12]}>
                            {DEPARTMENTS_CONFIG.map(dept => {
                                const members = diemDanhData
                                    .filter(m => (m.part || m.department) === dept.key)
                                    .sort((a, b) => getAttendancePriority(a) - getAttendancePriority(b));

                                return (
                                    <Col xs={24} sm={12} md={12} lg={dept.span} key={dept.key} style={{ minWidth: 0 }}>
                                        <div style={{
                                            background: dept.bg,
                                            border: `1px solid ${dept.border}`,
                                            borderRadius: '8px',
                                            padding: '6px 10px',
                                            marginBottom: '8px',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            <Text strong style={{ color: dept.color, fontSize: '13px' }}>
                                                {dept.key}
                                            </Text>
                                            <Text type="secondary" style={{ fontSize: '11px' }}>
                                                {members.length} người
                                            </Text>
                                        </div>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: `repeat(${dept.subCols}, 1fr)`,
                                            gap: '8px'
                                        }}>
                                            {members.map((member, index) => renderMemberCard(member, index, dept.color))}
                                        </div>
                                        {members.length === 0 && (
                                            <div style={{ textAlign: 'center', padding: '16px 0' }}>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>Không có nhân viên</Text>
                                            </div>
                                        )}
                                    </Col>
                                );
                            })}
                        </Row>
                        {diemDanhData.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '50px' }}>
                                <Text type="secondary">Không có dữ liệu điểm danh</Text>
                            </div>
                        )}
                    </>
                )}
            </Card>
    );

    return (
        <div style={{ padding: '16px 16px 16px 20px' }}>
            <Tabs
                defaultActiveKey="hom-nay"
                items={[
                    {
                        key: 'hom-nay',
                        label: 'Điểm danh hôm nay',
                        children: renderDiemDanhHomNay(),
                    },
                    {
                        key: 'bang-cham-cong',
                        label: 'Bảng chấm công',
                        children: <BangChamCongComponent />,
                    },
                ]}
            />
        </div>
    );
};

export default DiemDanhComponent;
